// Center-column input: samples first, then the dropzone (spec §2, §4.15).
import { FileAudio, FileText, Play, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Dropzone } from '@/components/dropzone'
import { SchemaEditor } from '@/components/schema-editor'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { REDACT_CATEGORIES, type Stop } from '@/lib/demos'
import { cn } from '@/lib/utils'

import type { Input, RunState } from '@/hooks/use-demo-run'
import type { DemoId, DemoParams, PlayConfig } from '@/shared/contract'

export function InputPanel({
  stop,
  input,
  state,
  params,
  config,
  onParams,
  onSample,
  onOwn,
  onRun,
}: {
  stop: Stop
  input: Input
  state: RunState
  params: DemoParams[DemoId]
  config?: PlayConfig
  onParams: (p: DemoParams[DemoId]) => void
  onSample: () => void
  onOwn: (f: File) => void
  onRun: () => void
}) {
  const [paramsValid, setParamsValid] = useState(true)
  useEffect(() => setParamsValid(true), [stop.demo])
  const busy = state.status === 'uploading' || state.status === 'running'
  const edited = JSON.stringify(params) !== JSON.stringify(state.params ?? stop.defaultParams)
  const own = input.source === 'own' && input.file.size > 0
  const canRun =
    !busy &&
    paramsValid &&
    (own ||
      edited ||
      (state.status === 'done' && state.result?.cached) ||
      state.status === 'error' ||
      stop.kind === 'none')
  const Icon = stop.kind === 'audio' ? FileAudio : FileText
  const maxPages = config?.limits.maxPages ?? 2
  const hint =
    stop.kind === 'audio'
      ? `MP3, WAV, M4A or WebM · up to ${config?.limits.maxAudioMinutes ?? 10} min`
      : `PDF, Word, PowerPoint, Excel or image · first ${maxPages} pages`

  return (
    <div className="flex flex-col gap-3">
      {stop.kind !== 'none' ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {stop.sample ? (
            <button
              type="button"
              onClick={onSample}
              aria-pressed={input.source === 'sample'}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
                input.source === 'sample'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted/40',
              )}
            >
              <Icon className="size-4 shrink-0 text-primary" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{stop.sample.label}</span>
                <span className="block text-xs text-muted-foreground">Sample · fictional</span>
              </span>
            </button>
          ) : null}
          <Dropzone
            accept={stop.accept}
            hint={
              own
                ? `${input.file.name} · ${input.file.size < 1e6 ? `${Math.round(input.file.size / 1e3)} KB` : `${(input.file.size / 1e6).toFixed(1)} MB`}`
                : hint
            }
            onFile={onOwn}
            className={cn('py-3', own && 'border-primary bg-primary/5')}
          />
        </div>
      ) : null}
      {stop.kind !== 'none' ? (
        <p className="text-[11px] text-muted-foreground">
          Your file is deleted after 1 hour. Do not upload real personal data.
        </p>
      ) : null}

      {own && input.upload?.truncatedFrom ? (
        <p className="rounded-md bg-clay-soft px-2 py-1 text-xs text-clay">
          We kept the first {input.upload.pages ?? maxPages} of {input.upload.truncatedFrom} pages
          (free playground limit).
        </p>
      ) : null}

      {stop.demo === 'fill-form' ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
          <FileText className="size-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-xs font-medium">Template · Certificate of Insurance</p>
            <p className="text-[11px] text-muted-foreground">
              Registered once for this playground; only your values are submitted when you run.
            </p>
          </div>
        </div>
      ) : null}

      <Params
        stop={stop}
        params={params}
        valid={paramsValid}
        onParams={onParams}
        onValidityChange={setParamsValid}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={onRun}
          disabled={!canRun}
          aria-label={own ? 'Run on your file' : edited ? 'Re-run with edits' : 'Run it live'}
        >
          {edited || state.status === 'error' ? (
            <RotateCcw data-icon="inline-start" />
          ) : (
            <Play data-icon="inline-start" />
          )}
          {own ? 'Run on my file' : edited ? 'Re-run with edits' : 'Run it live'}
        </Button>
        {state.status === 'done' && state.result?.cached && !edited ? (
          <span className="text-xs text-muted-foreground">Cached result · Run it live</span>
        ) : null}
        {state.result?.cost ? (
          <span className="font-mono text-xs tabular-nums text-muted-foreground sm:ml-auto">
            {state.result.cost.credits.toLocaleString()} credits · $
            {state.result.cost.usd.toFixed(3)}
          </span>
        ) : null}
        {state.status === 'error' ? (
          <span className="text-xs text-destructive">{state.error}</span>
        ) : null}
      </div>
    </div>
  )
}

function Params({
  stop,
  params,
  valid,
  onParams,
  onValidityChange,
}: {
  stop: Stop
  params: DemoParams[DemoId]
  valid: boolean
  onParams: (p: DemoParams[DemoId]) => void
  onValidityChange: (valid: boolean) => void
}) {
  switch (stop.demo) {
    case 'extract-invoice':
    case 'call-notes': {
      const p = params as DemoParams['extract-invoice']
      return (
        <details open className="schema-details rounded-lg border border-border p-2">
          <summary className="cursor-pointer text-xs font-medium">
            Extraction schema ·{' '}
            {Object.keys((p.schema?.properties as object | undefined) ?? {}).length} fields
          </summary>
          <div className="pt-2">
            <SchemaEditor value={p.schema ?? {}} onChange={(schema) => onParams({ schema })} />
          </div>
        </details>
      )
    }
    case 'redact': {
      const chosen = (params as DemoParams['redact']).categories ?? REDACT_CATEGORIES
      return (
        <fieldset className="flex flex-wrap gap-x-4 gap-y-1 rounded-lg border border-border p-2">
          <legend className="px-1 text-xs font-medium">Categories</legend>
          {REDACT_CATEGORIES.map((c) => (
            <label key={c} className="flex items-center gap-1.5 text-xs">
              <Checkbox
                checked={chosen.includes(c)}
                onCheckedChange={(on) =>
                  onParams({ categories: on ? [...chosen, c] : chosen.filter((x) => x !== c) })
                }
              />
              {c}
            </label>
          ))}
        </fieldset>
      )
    }
    case 'compose-invoice':
      return (
        <DataEditor
          key="compose"
          value={(params as DemoParams['compose-invoice']).data ?? {}}
          label="Data · edit the total and re-run"
          valid={valid}
          onChange={(data) => onParams({ data })}
          onValidityChange={onValidityChange}
        />
      )
    case 'fill-form':
      return (
        <DataEditor
          key="fill"
          value={(params as DemoParams['fill-form']).values ?? {}}
          label="Field values · deterministic, no source document"
          valid={valid}
          onChange={(values) => onParams({ values })}
          onValidityChange={onValidityChange}
        />
      )
    default:
      return null
  }
}

function DataEditor({
  value,
  label,
  valid,
  onChange,
  onValidityChange,
}: {
  value: Record<string, unknown>
  label: string
  valid: boolean
  onChange: (v: Record<string, unknown>) => void
  onValidityChange: (valid: boolean) => void
}) {
  // ponytail: a textarea, not Monaco; the data object is 25 lines.
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium">{label}</span>
      <Textarea
        defaultValue={JSON.stringify(value, null, 2)}
        spellCheck={false}
        className="h-[min(32rem,55vh)] min-h-72 field-sizing-fixed resize-y font-mono text-xs"
        onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value))
            onValidityChange(true)
          } catch {
            onValidityChange(false)
          }
        }}
      />
      {!valid ? <span className="text-xs text-destructive">Enter valid JSON to run.</span> : null}
    </label>
  )
}
